import { ApiMethod } from 'services/api';
import { Endpoint, Inputs } from './types';
import { formatJourneyCheckerResponse } from './utils';

export const endpoints: Endpoint[] = [
  {
    route: '/admin/journeyChecker',
    key: 'journeyChecker',
    opts: {
      allowIterable: {
        type: Inputs.Checkbox,
        default: false,
      },
      orgIds: {
        type: Inputs.TextArea,
      },
      parallelism: {
        default: 20,
        helpText:
          'Use if we run into performance issues to slow things down in order to avoid using too many threads',
        type: Inputs.Input,
      },
      perProjectLimit: {
        default: 100,
        type: Inputs.Input,
      },
      rules: {
        default: '',
        helpText:
          'One or more of: Cycle, NoSideEffects, StartTileFollowedByYNSplit, ChainedYNSplits, ChainedUserUpdates, ChainedChannelSubscription, ChainedMessageType, MultipleExitsToOneTile, IncorrectDelay, ChainedDelay, BranchNoSideEffects',
        label: 'Rules (optional)',
        type: Inputs.TextArea,
      },
    },
    method: ApiMethod.post,
    actions: [
      {
        title: 'Download to CSV',
        onClick: formatJourneyCheckerResponse,
      },
    ],
  },
  {
    route: '/admin/checkDisallowedTriggers',
    key: 'checkDisallowedTriggers',
    opts: {},
    method: ApiMethod.post,
  },
  {
    route: '/admin/clearStuckWorkflowUsers',
    key: 'clearStuckWorkflowUsers',
    opts: {
      organizationIdOpt: {
        type: Inputs.Input,
      },
      projectIdOpt: {
        type: Inputs.Input,
      },
      workflowIdOpt: {
        type: Inputs.Input,
      },
      tileTypeOpt: {
        type: Inputs.Input,
      },
      safeDeleteDays: {
        type: Inputs.Input,
      },
      safeDeleteHoursOpt: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
      handleAllDeletedNodes: {
        type: Inputs.Checkbox,
        default: false,
      },
      downloadStuckUsers: {
        type: Inputs.Checkbox,
        default: false,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/clearVisitorsFromPg',
    key: 'clearVisitorsFromPg',
    opts: {
      journeyId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/bulkResumeVisitorsAtNode',
    key: 'bulkResumeVisitorsAtNode',
    opts: {
      workflowId: {
        type: Inputs.Input,
      },
      nodeIdOpt: {
        type: Inputs.Input,
      },
      enteredBefore: {
        type: Inputs.Input,
      },
      runWithoutRequiringTriggerData: {
        type: Inputs.Checkbox,
        default: true,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/closeMigratedIndicesForOrg',
    key: 'closeMigratedIndicesForOrg',
    opts: {
      oldClusterId: {
        type: Inputs.Input,
      },
      orgId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/closeOldIndexAndCreateNewForProject',
    key: 'closeOldIndexAndCreateNewForProject',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/findConflictingProperties',
    key: 'findConflictingProperties',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/fixAllTemplatesMissingCampaignIds',
    key: 'fixAllTemplatesMissingCampaignIds',
    opts: {
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/fixTemplatesMissingCampaignIds',
    key: 'fixTemplatesMissingCampaignIds',
    opts: {
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/setDryRunForCampaign',
    key: 'setCampaignDryRun',
    opts: {
      campaignId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/setClusterForOrganization',
    key: 'setClusterForOrganization',
    opts: {
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
      newClusterId: {
        type: Inputs.Input,
      },
      orgId: {
        type: Inputs.Input,
      },
      bootstrapIndices: {
        type: Inputs.Checkbox,
        default: true,
      },
      bootstrapShards: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/slackAlertTest',
    key: 'slackAlertTest',
    opts: {},
    method: ApiMethod.post,
  },
  {
    route: '/admin/testFilterTile',
    key: 'testFilterTile',
    opts: {
      journeyId: {
        type: Inputs.Input,
      },
      tileId: {
        type: Inputs.Input,
      },
      userKey: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/bootstrap/:index',
    key: 'bootstrap',
    opts: {
      index: {
        type: Inputs.Input,
      },
      alias: {
        type: Inputs.Input,
      },
      shards: {
        type: Inputs.Input,
      },
      replicas: {
        type: Inputs.Input,
      },
      noRefresh: {
        type: Inputs.Checkbox,
        default: true,
      },
      clusterId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/bootstrapMappingForType',
    key: 'bootstrapMappingForType',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      clientDataType: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/bootstrapMappingsForTypes',
    key: 'bootstrapMappingsForTypes',
    opts: {
      projectIds: {
        type: Inputs.Input,
        label: 'projectIds, comma-separated',
      },
      dataTypes: {
        type: Inputs.Input,
        label: 'client dataTypes, comma-separated and camelCase (e.g., embeddedSend,embeddedClick)',
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/bootstrapMappingsForTypesByOrg',
    key: 'bootstrapMappingsForTypesByOrg',
    opts: {
      orgId: {
        type: Inputs.Input,
      },
      dataTypes: {
        type: Inputs.Input,
        label: 'client dataTypes, comma-separated and camelCase (e.g., embeddedSend,embeddedClick)',
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/remapIndex',
    key: 'remapIndex',
    opts: {
      index: {
        type: Inputs.Input,
      },
      clusterId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/breakoutTopic',
    key: 'createBreakoutTopic',
    opts: {
      kafkaClusterId: {
        type: Inputs.Input,
      },
      kafkaTopicName: {
        type: Inputs.Input,
      },
      projectIds: {
        type: Inputs.Input,
      },
      description: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/removePgStuckVisitors',
    key: 'removePgStuckVisitors',
    opts: {
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
      start: {
        type: Inputs.Input,
      },
      end: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/bulkExitVisitorsAtNode',
    key: 'bulkExitVisitorsAtNode',
    opts: {
      workflowId: {
        type: Inputs.Input,
      },
      nodeId: {
        type: Inputs.Input,
      },
      safeDeleteDays: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/folders/backfill',
    key: 'backfill',
    opts: {
      organizationId: {
        type: Inputs.Input,
      },
      folderType: {
        type: Inputs.Input,
      },
      parallelization: {
        type: Inputs.Input,
        default: 3000,
      },
      enabledOnly: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/deletionPolicy',
    key: 'createDeletionPolicy',
    opts: {
      id: {
        type: Inputs.Input,
      },
      includeNames: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
      enabled: {
        type: Inputs.Input,
      },
      global: {
        type: Inputs.Input,
      },
      ageInDays: {
        type: Inputs.Input,
      },
      slices: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/editDeletionPolicy',
    key: 'editDeletionPolicy',
    opts: {
      id: {
        type: Inputs.Input,
      },
      includeNames: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
      enabled: {
        type: Inputs.Input,
      },
      global: {
        type: Inputs.Input,
      },
      ageInDays: {
        type: Inputs.Input,
      },
      slices: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/updateRetention',
    key: 'updateRetention',
    opts: {
      id: {
        type: Inputs.Input,
      },
      updates: {
        type: Inputs.Input,
      },
      slices: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/removeDeletionPolicy',
    key: 'removeDeletionPolicy',
    opts: {
      id: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/standardSystemEventRetentionPolicy',
    key: 'standardSystemEventRetentionPolicy',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/standardSystemEventRetentionPolicyByOrg',
    key: 'standardSystemEventRetentionPolicyByOrg',
    opts: {
      orgId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/standardSystemEventRetentionPolicyForOrgs',
    key: 'standardSystemEventRetentionPolicyForOrgs',
    opts: {
      orgIds: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/admin/disableStandardSystemEventRetentionPolicy',
    key: 'disableStandardSystemEventRetentionPolicy',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/enableStandardSystemEventRetentionPolicy',
    key: 'enableStandardSystemEventRetentionPolicy',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/getCostOfAllListsForProject',
    key: 'getCostOfAllListsForProject',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/backfill/snippetReferences',
    key: 'snippetReferences',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/delivery-team/sesIdentityTags',
    key: 'getSESIdentityTags',
    opts: {
      identity: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/backfill/projectLocales',
    key: 'backfillProjectLocales',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      runAllProjects: {
        type: Inputs.Checkbox,
        default: false,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/backfill/resolveLocaleOverlap',
    key: 'resolveLocaleOverlap',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      orgId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: false,
      },
      runAllProjects: {
        type: Inputs.Checkbox,
        default: false,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/backfill/templateContent',
    key: 'templateContent',
    opts: {
      templateId: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
      orgId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: false,
      },
      runAllProjects: {
        type: Inputs.Checkbox,
        default: false,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/backfill/templateContentLocale',
    key: 'templateContentLocale',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      fromLocale: {
        type: Inputs.Input,
      },
      toLocale: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: false,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/campaigns/deactivate',
    key: 'deactivateCampaignLinks',
    opts: {
      campaignId: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/campaigns/deactivateBulk',
    key: 'deactivateCampaignLinksBulk',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      campaignIds: {
        type: Inputs.TextArea,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/deleteNotifications',
    key: 'deleteNotifications',
    opts: {
      startDateTime: {
        type: Inputs.Input,
      },
      endDateTime: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/cancelCampaignAlerts',
    key: 'cancelCampaignAlerts',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      campaignIds: {
        type: Inputs.Input,
      },
      journeyIds: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/bulkUpdateCampaignAlerts',
    key: 'bulkUpdateCampaignAlerts',
    opts: {
      projectId: {
        type: Inputs.Input,
      },
      campaignIds: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/commencePauseStream',
    key: 'commencePauseStream',
    opts: {
      kafkaCluster: {
        type: Inputs.Input,
      },
      kafkaTopics: {
        type: Inputs.TextArea,
      },
      consumerGroup: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/commenceUnpauseStream',
    key: 'commenceUnpauseStream',
    opts: {
      kafkaCluster: {
        type: Inputs.Input,
      },
      kafkaTopics: {
        type: Inputs.TextArea,
      },
      consumerGroup: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/isStreamPaused',
    key: 'isStreamPaused',
    opts: {
      kafkaCluster: {
        type: Inputs.Input,
      },
      kafkaTopics: {
        type: Inputs.TextArea,
      },
      consumerGroup: {
        type: Inputs.Input,
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/admin/runJobNow',
    key: 'runJobNow',
    opts: {
      jobName: {
        type: Inputs.Input,
        helpText:
          'NfpBillingDeduplicationJob, NfpBillingSnapshotJob, NfpBillingMonitoringJob, WhatsAppSenderDetailsSyncJob, WhatsAppSenderBusinessDetailsSyncJob, WhatsAppSenderApplicationIdSyncJob or UserRetentionCleanupJob',
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/generateNfpSnapshotEvents',
    key: 'generateNfpSnapshotEvents',
    opts: {
      datePST: {
        type: Inputs.Input,
        helpText: 'Date in PST timezone',
      },
      orgIds: {
        type: Inputs.Input,
        helpText: 'Comma separated list of orgIds for which snapshot billing events are generated',
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/admin/getES5toES8Mappings',
    key: 'getES5toES8Mappings',
    opts: {
      projectId: {
        type: Inputs.Input,
        helpText: 'An ES5 Project ID',
      },
    },
    method: ApiMethod.get,
  },
  {
    route: '/admin/creativeLibrary/migrateTemplate',
    key: 'migrateTemplate',
    opts: {
      templateId: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
  {
    route: '/admin/creativeLibrary/migrateTemplateLocale',
    key: 'migrateTemplateLocale',
    opts: {
      templateId: {
        type: Inputs.Input,
      },
      localeOpt: {
        type: Inputs.Input,
      },
      projectId: {
        type: Inputs.Input,
      },
      dryRun: {
        type: Inputs.Checkbox,
        default: true,
      },
    },
    method: ApiMethod.post,
  },
];
